import { useEffect, useRef, useCallback } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function ConfirmModal({
  isOpen,
  title,
  body,
  confirmLabel = 'Potvrdi',
  cancelLabel = 'Odustani',
  onConfirm,
  onCancel,
  variant = 'danger',
}) {
  const modalRef = useRef(null)
  const cancelRef = useRef(null)

  // Focus trap: start focus on the cancel (safe) button, cycle within modal
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onCancel()
        return
      }
      if (e.key !== 'Tab' || !modalRef.current) return

      const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [onCancel]
  )

  useEffect(() => {
    if (!isOpen) return

    // Focus the cancel (safe) button when modal opens
    const timer = setTimeout(() => {
      cancelRef.current?.focus()
    }, 0)

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const confirmClasses = {
    danger:
      'bg-danger text-on-danger hover:bg-danger-hover focus:ring-danger',
    warning:
      'bg-warn text-on-warn hover:bg-warn-hover focus:ring-warn',
    primary:
      'bg-primary text-on-primary hover:bg-primary-hover focus:ring-primary',
  }

  const confirmClass = confirmClasses[variant] || confirmClasses.danger

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        ref={modalRef}
        className="relative z-10 mx-4 w-full max-w-sm rounded-card bg-surface p-6 shadow-modal"
      >
        <h2
          id="confirm-modal-title"
          className="text-lg font-semibold text-fg"
        >
          {title}
        </h2>
        {body && <p className="mt-2 text-sm text-muted">{body}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-btn bg-surface-2 px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2-hover focus:outline-none focus:ring-2 focus:ring-faint focus:ring-offset-2"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`inline-flex items-center justify-center rounded-btn px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
